<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import CheckMark from "../../CheckMark.svelte";

  const dispatch = createEventDispatcher();
  
  export let packets = [];
  export let selectedPacketId = "";
  export let searchKey = "";
  export let isOpen = false;

  function selectPacket(packet: any) {
    dispatch('select', packet);
  }

  function closeModal() {
    dispatch('close');
  }

  function handlePacketKeydown(event: KeyboardEvent, packet: any) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectPacket(packet);
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeModal();
    }
  }

  function createNewPacket() {
    dispatch('create');
  }

  // Filter packets based on search
  $: filteredPackets = packets.filter(packet => 
    packet.packetName.toLowerCase().includes(searchKey.toLowerCase())
  );
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
  <div class="modal-overlay" on:click={closeModal} on:keydown={handleKeydown}>
    <div class="modal-content" on:click|stopPropagation>
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">Packets</h2>
        <button class="close-button" on:click={closeModal}>×</button>
      </div>

      <!-- Search and Add Section -->
      <div class="search-section">
        <input
          type="text"
          placeholder="Search Packets"
          class="search-input"
          bind:value={searchKey}
        />
        <button class="add-packet-button" on:click={createNewPacket}>
          + Add Packet
        </button>
      </div>

      <!-- Packets List -->
      <div class="packets-list">
        {#each filteredPackets as packet}
          <div 
            class="packet-item"
            class:selected={packet.id === selectedPacketId}
            on:click={() => selectPacket(packet)}
            on:keydown={(e) => handlePacketKeydown(e, packet)}
            tabindex="0"
            role="button"
            aria-label={`Select packet ${packet.packetName}`}
          >
            <div class="packet-info">
              <div class="packet-name">{packet.packetName}</div>
              <div class="packet-action">Select Packet</div>
            </div>
            <div class="packet-actions">
              <button 
                class="action-button edit"
                on:click|stopPropagation={() => dispatch('edit', packet)}
                title="Edit Packet"
              >
                <img src="/edit.png" alt="Edit" class="action-icon" />
              </button>
              <button 
                class="action-button delete"
                on:click|stopPropagation={() => dispatch('delete', packet)}
                title="Delete Packet"
              >
                <img src="/delete.png" alt="Delete" class="action-icon" />
              </button>
            </div>
          </div>
        {/each}

        {#if filteredPackets.length === 0}
          <div class="no-packets">
            {#if searchKey}
              No packets found matching "{searchKey}"
            {:else}
              No packets available. Create your first packet!
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 90%;
    max-width: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s;
  }

  .close-button:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  .search-section {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
  }

  .search-input {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s;
  }

  .search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .add-packet-button {
    background-color: #3b82f6;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    white-space: nowrap;
  }

  .add-packet-button:hover {
    background-color: #2563eb;
  }

  .packets-list {
    flex: 1;
    overflow-y: auto;
    padding: 0 24px 24px;
  }

  .packet-item {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px;
    margin: 8px 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    outline: none;
    height: auto;
    min-height: 0;
    box-sizing: border-box;
  }

  .packet-item:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .packet-item:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .packet-item.selected {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }

  .packet-info {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
  }

  .packet-name,
  .packet-action {
    position: static;
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    height: auto;
    line-height: 1.35;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    transform: none;
  }

  .packet-name {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  .packet-action {
    font-size: 14px;
    color: #3b82f6;
    font-weight: 500;
  }

  .packet-actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
  }

  .action-button {
    background: none;
    border: none;
    padding: 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .action-button:hover {
    background-color: #f3f4f6;
  }

  .action-button.delete:hover {
    background-color: #fef2f2;
  }

  .action-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }

  .action-button:hover .action-icon {
    opacity: 1;
  }

  .no-packets {
    text-align: center;
    padding: 40px 20px;
    color: #6b7280;
    font-style: italic;
  }

  /* Scrollbar styling */
  .packets-list::-webkit-scrollbar {
    width: 6px;
  }

  .packets-list::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  .packets-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .packets-list::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
</style>
